import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdHocCandidateInfo, AdHocCommitteeInfo, AHRService, PrcCandidateCommittee, ReadOnlyCommitteeGroup, ReviewerCommitteeMember } from '../../../domain/data-access-interfaces';
import { Result } from '../../../domain';
import * as committeeHelpers from '../committee-helpers';

/**
 * Admin management of PRC and Ad Hoc committee membership — the apr.CRUD_*
 * assignments that reviewer resolution reads when materializing workflow
 * tasks.
 *
 * Edits never auto-reconcile already-materialized tasks. Task drift on open
 * cases surfaces on the AHR case list and case modal (Academics → case →
 * Reconciliation), not here — its causes aren't committee-specific.
 */
@Component({
	selector: 'reviewer-committees',
	templateUrl: './reviewer-committees.component.html',
	styleUrls: ['./reviewer-committees.component.scss']
})
export class ReviewerCommitteesComponent implements OnInit {

	years: number[] = [];
	selectedYear: number | null = null;
	loading = false;
	activeTab: 'prc' | 'adhoc' | 'readonly' = 'prc';

	prc: PrcCandidateCommittee[] = [];
	adHoc: AdHocCommitteeInfo[] = [];
	adHocUnassigned: AdHocCandidateInfo[] = [];
	readOnlyGroups: ReadOnlyCommitteeGroup[] = [];

	/** URL fragment captured at load, consumed by the first successful refresh. */
	private pendingFragment: string | null = null;

	constructor(
		private readonly svc: AHRService,
		private readonly dialog: MatDialog,
		private readonly snackbar: MatSnackBar,
		private readonly route: ActivatedRoute,
		private readonly router: Router
	) { }

	ngOnInit(): void {
		this.pendingFragment = this.route.snapshot.fragment;

		// Restore the active tab from the URL up front — it doesn't depend on the
		// year load, so there's no reason to wait for it.
		const tab = this.route.snapshot.queryParamMap.get('tab');
		if (tab === 'prc' || tab === 'adhoc' || tab === 'readonly') this.activeTab = tab;

		// Same year bootstrapping as the review-dates page: the server's current
		// period (honors cutoff + simulated clock) is the period-ending year;
		// committee data keys on periodOfReviewId (= ending year + 1).
		Promise.all([
			this.svc.getReviewerCommitteeYears().catch((): number[] => []),
			this.svc.getCurrentPeriodOfReviewYear()
				.then(y => { const n = y ? +y : NaN; return isNaN(n) ? null : n + 1; })
				.catch(() => null)
		]).then(([years, currentYearId]) => {
			// The current period is always offered so committees can be set up
			// before any assignments exist for it.
			if (currentYearId !== null && !years.includes(currentYearId)) {
				years = [currentYearId, ...years].sort((a, b) => b - a);
			}
			// The URL carries the displayed period-ending year (id − 1); convert
			// back to the id here. A ?year= from the back button / a shared link /
			// a case-modal deep link wins over the current period so the selection
			// survives navigating away. A deep-linked year is offered even when it
			// has no assignments yet — that's often exactly why the link was followed.
			const shown = this.route.snapshot.queryParamMap.get('year');
			const fromUrl = shown !== null ? +shown + 1 : NaN;
			if (!isNaN(fromUrl) && !years.includes(fromUrl)) {
				years = [fromUrl, ...years].sort((a, b) => b - a);
			}
			this.years = years;
			if (years.length === 0) {
				this.selectedYear = null;
			} else {
				const restored = years.includes(fromUrl) ? fromUrl : null;
				this.selectYear(restored ?? (currentYearId !== null ? currentYearId : years[0]));
			}
		});
	}

	selectYear(year: number): void {
		this.selectedYear = year;
		// Mirror the choice into the URL as the displayed period-ending year (id − 1),
		// so a shared link reads the same value shown in the dropdown and the back
		// button restores it. replaceUrl keeps dropdown changes out of history — back
		// goes to the prior page, not each year you clicked through.
		this.setQueryParams({ year: year - 1 });
		this.loading = true;
		this.refresh();
	}

	selectTab(tab: 'prc' | 'adhoc' | 'readonly'): void {
		this.activeTab = tab;
		this.setQueryParams({ tab });
	}

	private setQueryParams(queryParams: Params): void {
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams,
			queryParamsHandling: 'merge',
			// Deep links from the case modal carry a #candidate-/#committee- anchor;
			// keep it in the URL so reload and copy-link retain it.
			preserveFragment: true,
			replaceUrl: true
		});
	}

	/** Re-fetch after an edit without the "Loading…" swap — replacing the arrays
	 *  in place keeps the page height (and scroll position) stable. */
	private reload(): void {
		if (this.selectedYear !== null) this.refresh();
	}

	private refresh(): void {
		const year = this.selectedYear!;
		Promise.all([
			this.svc.getPeerReviewCommittees(year),
			this.svc.getAdHocCommittees(year),
			this.svc.getReadOnlyCommitteeGroups(year)
		]).then(([prc, adHoc, readOnlyGroups]) => {
			this.prc = prc;
			this.adHoc = adHoc.committees;
			this.adHocUnassigned = adHoc.unassigned;
			this.readOnlyGroups = readOnlyGroups;
			this.loading = false;
			this.scrollToPendingFragment();
		}).catch(() => {
			this.loading = false;
			this.snackbar.open('Failed to load committees for this year.', undefined, { duration: 5000 });
		});
	}

	/** One-shot scroll to the row named by the URL fragment (deep links from the
	 *  case modal). Explicit rather than router anchorScrolling, which fires
	 *  before the async year load has rendered any rows. setTimeout lets the
	 *  freshly assigned arrays render first. */
	private scrollToPendingFragment(): void {
		const frag = this.pendingFragment;
		if (!frag) return;
		this.pendingFragment = null;
		setTimeout(() => {
			const el = document.getElementById(frag);
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				el.classList.add('anchor-highlight');
			}
		});
	}

	// trackBy functions so quiet refreshes update rows in place instead of
	// destroying and recreating them. Recreating ~100 <tr>s makes Chrome's
	// scroll anchoring compensate for each removed row, which scrolls the page
	// up by the height of every replaced row (to the top, when scrolled deep).
	trackByCandidate = (_: number, c: { candidateUserId: number }) => c.candidateUserId;
	trackByCommittee = (_: number, c: { committeeId: number }) => c.committeeId;
	trackByGroup = (_: number, g: { groupId: number }) => g.groupId;
	trackByUser = (_: number, m: { userId: number }) => m.userId;

	/** Open the person picker; run `then` only when someone was chosen. */
	private pickPerson(then: (userId: number) => void, excludeIds: number[] = [], title?: string): void {
		committeeHelpers.pickPerson(this.dialog, then, excludeIds, title);
	}

	/** Reload on success, snack the error otherwise. */
	private apply(op: Promise<Result<void, string>>): void {
		committeeHelpers.applyResult(op, this.snackbar, () => this.reload());
	}

	// ── PRC ──

	/** New candidate rows only exist once they have a reviewer, so adding a
	 *  candidate immediately asks for their first reviewer (who writes). */
	addPrcCandidate(): void {
		this.pickPerson(candidateUserId =>
			this.pickPerson(reviewerUserId =>
				this.apply(this.svc.upsertPrcReviewer(this.selectedYear!, candidateUserId, reviewerUserId, true)),
				[candidateUserId], 'Select the First Reviewer'),
			this.prc.map(c => c.candidateUserId), 'Select the Candidate');
	}

	addPrcReviewer(c: PrcCandidateCommittee): void {
		// The first reviewer becomes the writer by default.
		const writesReview = c.reviewers.length === 0;
		this.pickPerson(reviewerUserId =>
			this.apply(this.svc.upsertPrcReviewer(this.selectedYear!, c.candidateUserId, reviewerUserId, writesReview)),
			c.reviewers.map(r => r.userId), `Add a Reviewer for ${c.candidateName}`);
	}

	makePrcWriter(c: PrcCandidateCommittee, r: ReviewerCommitteeMember): void {
		this.apply(this.svc.upsertPrcReviewer(this.selectedYear!, c.candidateUserId, r.userId, true));
	}

	removePrcReviewer(c: PrcCandidateCommittee, r: ReviewerCommitteeMember): void {
		this.apply(this.svc.removePrcReviewer(this.selectedYear!, c.candidateUserId, r.userId));
	}

	// ── Ad Hoc ──

	createAdHocCommittee(): void {
		this.svc.createAdHocCommittee(this.selectedYear!)
			.then(() => this.reload())
			.catch(() => this.snackbar.open('Failed to create committee.', undefined, { duration: 5000 }));
	}

	deleteAdHocCommittee(c: AdHocCommitteeInfo): void {
		this.apply(this.svc.deleteAdHocCommittee(this.selectedYear!, c.committeeId));
	}

	addAdHocMember(c: AdHocCommitteeInfo): void {
		// The first member becomes the chair by default.
		const writesReview = c.members.length === 0;
		this.pickPerson(userId =>
			this.apply(this.svc.upsertAdHocMember(this.selectedYear!, c.committeeId, userId, writesReview)),
			c.members.map(m => m.userId), `Add a Member to Committee ${c.committeeId}`);
	}

	makeAdHocChair(c: AdHocCommitteeInfo, m: ReviewerCommitteeMember): void {
		this.apply(this.svc.upsertAdHocMember(this.selectedYear!, c.committeeId, m.userId, true));
	}

	removeAdHocMember(c: AdHocCommitteeInfo, m: ReviewerCommitteeMember): void {
		this.apply(this.svc.removeAdHocMember(this.selectedYear!, c.committeeId, m.userId));
	}

	// Template-visible re-exports of the shared helpers.
	adHocChair = committeeHelpers.adHocChair;
	adHocCommitteeLabel = committeeHelpers.adHocCommitteeLabel;

	/** Assigning moves the candidate if they were on another committee this year. */
	assignAdHocCandidate(c: AdHocCommitteeInfo): void {
		this.pickPerson(candidateUserId =>
			this.apply(this.svc.assignAdHocCandidate(this.selectedYear!, c.committeeId, candidateUserId)),
			c.candidates.map(cand => cand.userId), `Assign a Candidate to Committee ${c.committeeId}`);
	}

	/** Assign from the unassigned list. The <select> is reset because the row it
	 *  belongs to disappears on reload — leaving it set would flash the stale
	 *  choice onto whichever row takes its place. */
	assignUnassignedCandidate(cand: AdHocCandidateInfo, select: HTMLSelectElement): void {
		const committeeId = +select.value;
		select.selectedIndex = 0;
		if (!committeeId) return;
		this.apply(this.svc.assignAdHocCandidate(this.selectedYear!, committeeId, cand.userId));
	}

	/** Removing an assignment doesn't remove the candidate from the page: if
	 *  their case still needs a committee the reload lists them as unassigned,
	 *  and if it doesn't they drop off entirely. Both fall out of the reload. */
	unassignAdHocCandidate(c: AdHocCommitteeInfo, cand: { userId: number }): void {
		this.apply(this.svc.unassignAdHocCandidate(this.selectedYear!, c.committeeId, cand.userId));
	}

	// ── Read-only access ──

	/** With no group yet for the year, the server allocates one on the first grant. */
	addReadOnlyReviewer(kind: 'prc' | 'adhoc', group?: ReadOnlyCommitteeGroup): void {
		const existing = kind === 'prc' ? group?.prcUsers : group?.adHocUsers;
		this.pickPerson(userId =>
			this.apply(this.svc.addReadOnlyReviewer(this.selectedYear!, kind, userId, group?.groupId)),
			(existing || []).map(m => m.userId), 'Add a Read-Only Reviewer');
	}

	removeReadOnlyReviewer(kind: 'prc' | 'adhoc', m: ReviewerCommitteeMember): void {
		this.apply(this.svc.removeReadOnlyReviewer(this.selectedYear!, kind, m.userId));
	}

	addReadOnlyCandidate(group: ReadOnlyCommitteeGroup): void {
		this.pickPerson(candidateUserId =>
			this.apply(this.svc.addReadOnlyCandidate(group.groupId, candidateUserId)),
			group.candidates.map(c => c.userId), 'Add a Read-Only Candidate');
	}

	removeReadOnlyCandidate(group: ReadOnlyCommitteeGroup, cand: { userId: number }): void {
		this.apply(this.svc.removeReadOnlyCandidate(group.groupId, cand.userId));
	}
}
